<template>
  <transition name="fade-modal">
    <div
      v-if="active"
      ref="modalwrapper"
      class="e6-modal-wrapper"
      :class="{ 'modal-wrapper--active': active }"
      :style="{ 'z-index': zIndex }"
      tabindex="0"
      @keydown.esc="close"
    >
      <div
        ref="innermodalwrapper"
        class="e6-modal-wrapper__inner"
        @mousedown.self="close"
      >
        <div
          ref="modalcontainer"
          class="e6-modal-container"
          tabindex="-1"
        >
          <div
            class="e6-modal-header"
            :style="{ 'background-color': bgColor }"
          >
            {{ title }}
            <button
              :aria-label="'Stäng fönstret: ' + title"
              class="e6-close-button"
              type="button"
              @click="close"
            >
              <svgicon
                class="e6-close-icon"
                name="Controls/Close"
              />
            </button>
          </div>
          <div
            :style="{ 'text-align': alignContent, 'background-color': bgColor }"
            class="e6-modal-content"
            :class="{ 'modal-content--centered': centerMobile }"
          >
            <div class="e6-modal-content__inner">
              <slot name="content" />
            </div>
          </div>
          <footer
            v-if="hasFooterSlot"
            class="e6-modal-footer"
          >
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import svgicon from 'vue-svgicon'
import '#/Controls/Close'

const KEY_UP = ['ArrowUp', 'Home', 'PageUp']
const KEY_DOWN = ['ArrowDown', 'End', 'PageDown']
const KEY_SCROLL = KEY_UP.concat(KEY_DOWN)
const SCROLL_BLOCK_CLASS = 'modal--overflow-y-hidden-mobile'
const NOT_PASSIVE = {
  passive: false
}
export default {
  name: 'LegacyModal',
  components: {
    svgicon
  },
  model: {
    prop: 'active',
    event: 'openstate'
  },
  props: {
    active: {
      type: Boolean,
      default: true
    },
    centerMobile: {
      type: Boolean,
      default: false
    },
    zIndex: {
      type: Number,
      default: 100
    },
    alignContent: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    containFocus: {
      type: Boolean,
      default: true
    },
    bgColor: {
      type: String,
      default: '#fff'
    },
    trackingName: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      hasFocusListener: false,
      returnFocusElement: null
    }
  },
  computed: {
    parsedUrl () {
      return this.url
        ? `https://www.youtube-nocookie.com/embed/${this.url.split(/[=/]/g).pop()}?modestbranding=1&rel=0`
        : ''
    },
    hasFooterSlot () {
      return !!this.$slots['footer']
    }
  },
  watch: {
    active () {
      if (this.active) {
        this.openHandler()
      } else {
        this.closeHandler()
      }
    },
    containFocus () {
      if (this.containFocus && this.active) {
        this.captureFocus()
      } else {
        this.removeFocusCapture()
      }
    }
  },
  beforeDestroy () {
    this.closeHandler()
  },
  mounted () {
    if (this.active) {
      this.openHandler()
    }
  },
  methods: {
    touchHandler (e) {
      if (e.type === 'touchmove') {
        e.preventDefault()
      }
    },
    close (e) {
      if (e.type !== 'keydown' && e.clientX !== 0 && e.clientY !== 0) {
        // as we have a click element (by mouse or touch) no need really to return the focus
        this.returnFocusElement = null
      }

      this.$emit('openstate', false)
    },
    closeHandler () {
      this.unlockScroll()
      this.removeFocusCapture()
      this.returnFocusElement && this.returnFocusElement.focus()
      document.documentElement.classList.remove(SCROLL_BLOCK_CLASS)
    },
    openHandler () {
      this.captureFocus()
      this.lockScroll()
      this.$nextTick(() => {
        this.returnFocusElement = document.activeElement
        this.$refs.modalcontainer.focus()
        this.trackOpen()
      })

      document.documentElement.classList.add(SCROLL_BLOCK_CLASS)
    },
    captureFocus () {
      if (this.containFocus && !this.hasFocusListener) {
        window.addEventListener('focusin', this.focusinEventHandler)
        this.hasFocusListener = true
      }
    },
    removeFocusCapture () {
      window.removeEventListener('focusin', this.focusinEventHandler)
      this.hasFocusListener = false
    },
    focusinEventHandler (e) {
      this.focusHandler(e.target)
    },
    focusHandler (el) {
      if (this.$refs.modalwrapper !== el && !this.$refs.modalwrapper.contains(el)) {
        const focusElements = this.getFocusElements()
        if (focusElements.length) {
          if (this.$refs.modalwrapper !== focusElements[0] && !this.$refs.modalwrapper.contains(focusElements[0])) {
            console.log('Could not find a suitable focustarget?', focusElements, this.$refs.modalwrapper)
            return
          }
          focusElements[0].focus()
        }
      }
    },
    getComposedPath (e) {
      if (e.composedPath) {return e.composedPath()}

      let path = []
      let el = e.target

      while (el) {
        path.push(el)

        if (el.tagName === 'HTML') {
          path.push(document)
          path.push(window)
          return path
        }

        el = el.parentElement
      }

      return path
    },
    lockScroll () {
      addEventListener('wheel', this.inputDeviceHandler, NOT_PASSIVE)
      addEventListener('keydown', this.inputDeviceHandler)
      addEventListener('touchmove', this.inputDeviceHandler, NOT_PASSIVE)
    },
    unlockScroll () {
      removeEventListener('wheel', this.inputDeviceHandler, NOT_PASSIVE)
      removeEventListener('keydown', this.inputDeviceHandler)
      removeEventListener('touchmove', this.inputDeviceHandler, NOT_PASSIVE)
    },
    shouldBlockScroll (e) {
      if (e.type === 'keydown' && !KEY_SCROLL.includes(e.key)) {
        return false
      }

      const composedPath = this.getComposedPath(e)

      for (let i = 0; i < composedPath.length; i++) {
        let el = composedPath[i]

        // don't try to scroll outer elements
        if ([document, document.documentElement, this.$refs.innermodalwrapper].includes(el)) {
          return true
        }

        if (el.clientHeight < el.scrollHeight && this.$refs.modalwrapper.contains(el)) {
          //We are in a scrollable element, inside the modal
          if (e.type === 'keydown') {
            if (KEY_UP.includes(e.key)) {
              // key indicates upward scroll
              if (el.scrollTop === 0) {
                // we are already at the top of the scrollable element
                return true
              }
            } else if (KEY_DOWN.includes(e.key)) {
              // key indicates downward scroll
              if (el.scrollTop + el.clientHeight >= el.scrollHeight) {
                // we are already at the bottom of the scrollable element
                return true
              }
            }
          }

          // no match for up/down-keys or it is a scroll event
          // let the event propagate as usual (enables scrolling of inner containers)
          return false
        }
      }

      // Didn't find a scrollable element within the modal, so block all scroll-action
      return true
    },
    inputDeviceHandler (e) {
      if (e.type === 'keydown') {
        if (e.key === 'ESC') {
          //Always close on ESC, even if focus is elsewhere
          this.close()
        }

        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) || e.target.isContentEditable) {
          // If the focus is on a formfield or an editable element, keep keyboardnavigation intact
          return
        }
      }

      if (
        ((e.type === 'keydown' && KEY_SCROLL.includes(e.key)) || e.type === 'wheel' || e.type === 'touchmove') &&
        this.shouldBlockScroll(e)
      ) {
        e.preventDefault()
      }
    },
    getFocusElements () {
      return this.$refs.modalwrapper.querySelectorAll(
        '[tabindex]:not([tabindex="-1"]), button, [href], input, select, textarea'
      )
    },
    trackOpen () {
      if (this.trackingName === '') {
        // Not mandatory, but only track if has content
        return
      }

      const track = {
        event: 'nav-modal-open',
        modalHeader: this.trackingName
      }

      if (window && window.dataLayer) {
        window.dataLayer.push(track)
      }
    }
  }
}
</script>

<style lang="scss">
@include media-breakpoint-down(md) {
  html.e6-modal--overflow-y-hidden-mobile {
    overflow-y: hidden;
  }
}

.e6-modal-wrapper {
  background-color: rgba(0, 0, 0, 0.6);
  height: 100%;
  left: 0;
  outline: none;
  pointer-events: none;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 6;

  &__inner {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    overflow: hidden;
    pointer-events: auto;
    width: 100%;
  }
}

.e6-modal-container {
  background-color: $color-white;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  pointer-events: auto;
  width: 100%;
  z-index: inherit;
  text-align: left;

  @include media-breakpoint-up(md) {
    border-radius: 0.25rem;
    box-shadow: 0 0.2rem 1rem 0 rgba(0, 0, 0, 0.6);
    height: auto;
    max-height: 95vh;
    margin: 1.5rem;
    max-width: 38rem;
  }

  &:focus {
    //only focusable by js and only to contain scroll by keyboard
    outline: 0;
  }
}

.e6-modal-footer {
  padding: 1.125rem 1rem;
  position: relative;
  position: sticky;
  bottom: 0;
  z-index: 2;
  border-top: 0.0625rem solid $color-grey-special;
  background: $color-white;

  @include media-breakpoint-up(md) {
    padding: 1rem 1.5rem;
  }
}

.e6-modal-header {
  @include font(rubrik, 2, bold);

  background: $color-white;
  border-bottom: 0.0625rem solid $color-grey-special;
  // position: relative;
  position: sticky;
  display: flex;
  align-items: center;
  justify-content: space-between;
  top: 0;
  z-index: 2;
  text-align: left;
  padding: 1rem 4rem 1rem 1rem;

  @include media-breakpoint-up(md) {
    padding: 1rem 4rem 1rem 1.5rem;
  }

  .e6-close-button {
    width: 2rem;
    height: 2rem;
    position: absolute;
    background: none;
    border: 0;
    cursor: pointer;
    right: 1rem;
    top: 1rem;
    padding: 0;
    z-index: 400;
    outline: none;
    background-color: $color-red-special;
    border-radius: 50%;

    .e6-close-icon {
      fill: $color-secondary-cta-text;
      width: 1.25rem;
      height: 1.25rem;
    }

    @include media-breakpoint-up(md) {
      &:hover {
        background-color: $color-pink-light;

        .close-icon {
          fill: $color-white;
        }
      }
    }
  }
}

.e6-modal-content {
  @include font(text, 3, normal);

  padding: 1rem 1rem 1.5rem;
  z-index: 1;
  flex: 1 1 auto;

  @include media-breakpoint-up(md) {
    padding: 1.5rem 1.5rem 2rem;
  }

  &--centered {
    display: flex;
    align-items: center;
    flex-direction: column;
    flex-grow: 1;
    justify-content: center;
  }

  .e6-modal-content__inner {
    > :first-child {
      margin-top: 0;
    }

    > :last-child {
      margin-bottom: 0;
    }
  }
}

.fade-modal-enter-active,
.fade-modal-leave-active {
  transition: 0.1s ease-in-out, top 1s ease;

  .modal-container {
    transition: transform 0.3s cubic-bezier(0.2, 0, 0.37, 1.38), box-shadow 0.3s ease-in-out;
  }
}

.fade-modal-leave-active {
  transition-delay: 0.05s;
  transition-duration: 0.15s;
}

.fade-modal-enter,
.fade-modal-leave-to {
  opacity: 0;

  .modal-container {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    transform: scale(0.75) translateY(25%);
  }
}

.fade-modal-enter-to,
.fade-modal-leave {
  opacity: 1;

  .modal-container {
    box-shadow: 0 0.2rem 1rem 0 rgba(0, 0, 0, 0.6);
    transform: scale(1) translateY(0);
  }
}
</style>
